import { useCallback, useState } from 'react';
import { messageApi } from '@formosa/shared/api/im';
import { useChat } from '../../chat/ChatProvider';

/**
 * 會話內訊息搜尋面板。
 */
export default function MessageSearchPanel({ conversationId, open, onClose }) {
  const { jumpToMessage, state } = useChat();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [oldestHitSeq, setOldestHitSeq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  const runSearch = useCallback(
    async (query, beforeSeq) => {
      const keyword = (query ?? q).trim();
      if (!keyword || !conversationId) return;
      setLoading(true);
      setError('');
      try {
        const page = await messageApi.search(conversationId, {
          q: keyword,
          beforeSeq: beforeSeq || undefined,
          limit: 20,
        });
        const rows = page?.items || [];
        if (beforeSeq) {
          setItems((prev) => [...prev, ...rows]);
        } else {
          setItems(rows);
        }
        setHasMore(!!page?.hasMore);
        setOldestHitSeq(page?.oldestHitSeq ?? null);
        setActiveQuery(page?.query || keyword);
      } catch (err) {
        setError(err?.message || '搜尋失敗');
        if (!beforeSeq) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [conversationId, q]
  );

  if (!open) return null;

  const summaries = state.participantSummaries || {};

  return (
    <div className="chat-search-panel" role="dialog" aria-label="搜尋訊息">
      <div className="chat-search-panel__head">
        <strong>搜尋訊息</strong>
        <button type="button" className="chat-link-btn" onClick={onClose}>
          關閉
        </button>
      </div>
      <form
        className="chat-search-panel__form"
        onSubmit={(e) => {
          e.preventDefault();
          runSearch(q, null);
        }}
      >
        <input
          type="search"
          value={q}
          placeholder="關鍵字…"
          maxLength={64}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <button type="submit" disabled={loading || !q.trim()}>
          搜尋
        </button>
      </form>
      {error ? <div className="chat-search-panel__error">{error}</div> : null}
      <div className="chat-search-panel__list">
        {loading && !items.length ? <div className="chat-state">搜尋中…</div> : null}
        {!loading && activeQuery && !items.length ? (
          <div className="chat-state">沒有符合「{activeQuery}」的訊息</div>
        ) : null}
        <ul>
          {items.map((msg) => {
            const name =
              summaries[msg.senderMemberId]?.displayName ||
              (msg.senderMemberId != null ? `用戶 ${msg.senderMemberId}` : '系統');
            const preview =
              msg.text ||
              msg.fileName ||
              msg.locationName ||
              msg.emoteCode ||
              msg.messageType ||
              '';
            return (
              <li key={msg.serverMessageId || msg.seq}>
                <button
                  type="button"
                  className="chat-search-panel__hit"
                  onClick={() => {
                    jumpToMessage?.(conversationId, msg).catch?.(() => {});
                    onClose?.();
                  }}
                >
                  <span className="chat-search-panel__hit-meta">
                    {name}
                    {msg.createdAt ? ` · ${formatHitTime(msg.createdAt)}` : ''}
                  </span>
                  <span className="chat-search-panel__hit-text">{preview}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {hasMore ? (
          <button
            type="button"
            className="chat-link-btn"
            disabled={loading || oldestHitSeq == null}
            onClick={() => runSearch(activeQuery || q, oldestHitSeq)}
          >
            載入更多
          </button>
        ) : null}
      </div>
    </div>
  );
}

function formatHitTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}
